import React, { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import useIntelSearch from './useIntelSearch';
import LeftPanel from './LeftPanel';
import SearchArea from './SearchArea';
import FilterPanel from './FilterPanel';
import SaveSearchModal from './SaveSearchModal';
import IntelSearchStorageService from '../../../services/IntelSearchStorageService';
import UserService from '../../../services/UserService';
import CandidateDetails from '../CandidateDetails';
import classes from './styles.module.scss';

export default function IntelSearch() {
  const { data: user } = useQuery({
    queryKey: ['me'],
    queryFn: UserService.getMe,
  });
  const currentUserId = user?.id;
  const hasLoadedFromUrlRef = useRef(false);

  // State to trigger left panel refresh
  const [leftPanelRefreshTrigger, setLeftPanelRefreshTrigger] =
    React.useState(0);

  // Callback to refresh left panel when a search is added to recent
  const handleRecentSearchAdded = React.useCallback(() => {
    setLeftPanelRefreshTrigger((prev) => prev + 1);
  }, []);

  // State for CandidateDetails
  const [isCandidateDetailsVisible, setIsCandidateDetailsVisible] =
    React.useState(false);

  const {
    // UI State
    isLeftPanelCollapsed,
    toggleLeftPanel,

    // Search State
    searchInput,
    setSearchInput,
    searchChips,
    addSearchChip,
    removeSearchChip,

    // Filter State
    filters,
    updateFilter,
    updateFilters,
    resetFilters,
    activeFiltersCount,

    // Section State
    activeSections,
    toggleSection,
    sectionHasNonDefaultValues,

    // Results State
    results,
    isLoading,
    pagination,
    setPagination,
    sortBy,
    setSortBy,

    // Selection State
    selectedCandidates,
    toggleCandidateSelection,

    // Actions
    performSearch,

    // Save/Load
    getCurrentSearchState,
    loadSearch,
    showSaveSearchModal,
    openSaveSearchModal,
    closeSaveSearchModal,
    activeSavedSearch,
    revertToSaved,
    clearActiveSavedSearch,
    activeJob,
    setActiveJob,
    clearActiveJob,
  } = useIntelSearch(currentUserId, handleRecentSearchAdded);

  // Handle saving a search
  const handleSaveSearch = ({ name, description, projectId }) => {
    /* eslint-disable no-console */
    console.log('💾 Saving search:', { name, description, projectId });

    if (currentUserId) {
      // Ensure service is initialized
      IntelSearchStorageService.init(currentUserId);

      const searchPayload = getCurrentSearchState();
      const savedSearch = IntelSearchStorageService.saveSearch({
        name,
        description,
        searchPayload,
        projectId,
      });
      console.log('✅ Search saved:', savedSearch);
      console.log(
        '📋 All searches:',
        IntelSearchStorageService.getSavedSearches()
      );
      closeSaveSearchModal();
      // Trigger left panel to refresh
      setLeftPanelRefreshTrigger((prev) => prev + 1);
    }
    /* eslint-enable no-console */
  };

  // Load saved search and job from URL parameters on mount
  useEffect(() => {
    if (!currentUserId || hasLoadedFromUrlRef.current) {
      return;
    }

    // Get parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const searchId = urlParams.get('searchId');
    const jobId = urlParams.get('jobId');
    const jobTitle = urlParams.get('jobTitle');

    // Handle job parameters
    if (jobId && jobTitle) {
      setActiveJob({
        id: parseInt(jobId, 10),
        title: decodeURIComponent(jobTitle),
      });
    }

    // Handle saved search parameter
    if (searchId) {
      // Initialize storage service
      IntelSearchStorageService.init(currentUserId);

      // Get all saved searches and find the one matching the ID
      const savedSearches = IntelSearchStorageService.getSavedSearches();
      const savedSearch = savedSearches.find((s) => s.id === searchId);

      if (savedSearch && savedSearch.searchPayload) {
        // Mark as loaded to prevent multiple loads
        hasLoadedFromUrlRef.current = true;

        // Update last used timestamp
        IntelSearchStorageService.updateLastUsed(searchId);

        // Load the search
        loadSearch(savedSearch.searchPayload, true, {
          id: savedSearch.id,
          name: savedSearch.name,
        });
      }
    }

    // Clean up URL parameters after loading
    if (searchId || jobId || jobTitle) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      hasLoadedFromUrlRef.current = true;
    }
  }, [currentUserId, loadSearch, setActiveJob]);

  // Get selected candidate from results
  const selectedCandidateId =
    selectedCandidates.length > 0 ? selectedCandidates[0] : null;
  const selectedCandidate = selectedCandidateId
    ? results.find((c) => c.id === selectedCandidateId)
    : null;

  // Update visibility when candidate selection changes
  useEffect(() => {
    if (selectedCandidate?._original) {
      setIsCandidateDetailsVisible(true);
    } else {
      setIsCandidateDetailsVisible(false);
    }
  }, [selectedCandidate]);

  // Handle closing CandidateDetails
  const handleCloseCandidateDetails = () => {
    setIsCandidateDetailsVisible(false);
    // Clear selection
    if (selectedCandidates.length > 0) {
      toggleCandidateSelection(selectedCandidates[0]);
    }
  };

  return (
    <>
      <div className={classes.IntelSearchScreen}>
        <LeftPanel
          isCollapsed={isLeftPanelCollapsed}
          onToggle={toggleLeftPanel}
          onLoadSearch={loadSearch}
          currentUserId={currentUserId}
          refreshTrigger={leftPanelRefreshTrigger}
        />
        <SearchArea
          isLeftPanelCollapsed={isLeftPanelCollapsed}
          onToggleLeftPanel={toggleLeftPanel}
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          searchChips={searchChips}
          onAddChip={addSearchChip}
          onRemoveChip={removeSearchChip}
          results={results}
          isLoading={isLoading}
          pagination={pagination}
          setPagination={setPagination}
          selectedCandidates={selectedCandidates}
          onToggleCandidateSelection={toggleCandidateSelection}
          onSearch={performSearch}
          sortBy={sortBy}
          setSortBy={setSortBy}
          onSaveSearch={openSaveSearchModal}
          activeSavedSearch={activeSavedSearch}
          onRevertToSaved={revertToSaved}
          onClearActiveSavedSearch={clearActiveSavedSearch}
          activeJob={activeJob}
          onClearActiveJob={clearActiveJob}
        />
        <FilterPanel
          filters={filters}
          onUpdateFilter={updateFilter}
          onUpdateFilters={updateFilters}
          onResetFilters={resetFilters}
          activeFiltersCount={activeFiltersCount}
          activeSections={activeSections}
          onToggleSection={toggleSection}
          sectionHasNonDefaultValues={sectionHasNonDefaultValues}
        />
      </div>

      {/* Save Search Modal */}
      {showSaveSearchModal && (
        <SaveSearchModal
          show={showSaveSearchModal}
          handleClose={closeSaveSearchModal}
          onSave={handleSaveSearch}
          projects={
            currentUserId ? IntelSearchStorageService.getProjects() : []
          }
        />
      )}

      {/* Candidate Details */}
      {selectedCandidate?._original && (
        <CandidateDetails
          isVisible={isCandidateDetailsVisible}
          setIsVisible={handleCloseCandidateDetails}
          refetch={() => {}}
          candidate={selectedCandidate._original}
          itemIds={results.map((candidate) => candidate.id)}
          setCurrentItemId={(itemId) => {
            const candidate = results.find((c) => c.id === itemId);
            if (candidate) {
              toggleCandidateSelection(itemId);
            }
          }}
        />
      )}
    </>
  );
}
